import { gameToScreenCoords } from "@Components/game/session-results/feedback/visualization/constants";
import "@pixi/events";
import type { Character as ReplayCharacter, ReplayData } from "@Shared/types/ReplayData";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

const LOOK_DIRECTION_EPS_SQ = 1e-8;
const REPLAY_RENDER_INTERVAL_MS = 33;

const computeVisionConeRotationRadians = (
  character: ReplayCharacter,
  time: number,
  position: { x: number; y: number; z: number },
  interpolateValue: (dataArray: any[], time: number, ...keys: string[]) => any
): number => {
  const lookFrames = character.lookFrames;
  if (lookFrames && lookFrames.length > 0) {
    const look = interpolateValue(lookFrames, time, "x", "y", "z");
    const screenP = gameToScreenCoords(position.x, position.y);
    const screenL = gameToScreenCoords(look.x, look.y);
    const dx = screenL.x - screenP.x;
    const dy = screenL.y - screenP.y;
    if (dx * dx + dy * dy > LOOK_DIRECTION_EPS_SQ) {
      return Math.atan2(dy, dx) - Math.PI;
    }
  }

  if (character.rotation && character.rotation.length > 0) {
    const rot = interpolateValue(character.rotation, time, "angle");
    return rot.angle * (Math.PI / 180);
  }

  return 0;
};

interface UseReplayRendererProps {
  replayData: ReplayData | null;
  progress: number;
  isPlaying: boolean;
}

interface CharacterState {
  id: string;
  x: number;
  y: number;
  z: number;
  rotation: number;
  team: number;
  jerseyNumber: number;
  isPlayer: boolean;
}

const useReplayRenderer = ({ replayData, progress, isPlaying }: UseReplayRendererProps) => {
  const [characterStates, setCharacterStates] = useState<CharacterState[]>([]);

  const [fieldDimensions] = useState({ width: 1440, height: 900 });
  const animationFrameRef = useRef<number>();
  const lastUpdateTimeRef = useRef<number>(0);
  const progressRef = useRef<number>(progress);
  const lastReplayDataRef = useRef<ReplayData | null | undefined>(undefined);

  useLayoutEffect(() => {
    progressRef.current = progress;
  }, [progress]);

  const interpolateCharacterData = (character: ReplayCharacter, time: number) => {
    const position = interpolateValue(character.position, time, "x", "y", "z");
    const rotationRadians = computeVisionConeRotationRadians(character, time, position, interpolateValue);

    return {
      id: character.id,
      x: position.x,
      y: position.y,
      z: position.z,
      rotation: rotationRadians,
      team: character.team,
      jerseyNumber: character.jerseyNumber,
      isPlayer: character.isPlayer,
    };
  };

  const interpolateValue = (dataArray: any[], time: number, ...keys: string[]) => {
    if (!dataArray || dataArray.length === 0) {
      const result: any = {};
      keys.forEach(key => {
        result[key] = 0;
      });
      return result;
    }

    let index = dataArray.findIndex(item => item.time >= time);

    if (index === -1) {
      const lastItem = dataArray[dataArray.length - 1];
      const result: any = {};
      keys.forEach(key => {
        result[key] = lastItem[key] || 0;
      });
      return result;
    }

    if (index === 0) {
      const firstItem = dataArray[0];
      const result: any = {};
      keys.forEach(key => {
        result[key] = firstItem[key] || 0;
      });
      return result;
    }

    const prevItem = dataArray[index - 1];
    const nextItem = dataArray[index];
    const timeDiff = nextItem.time - prevItem.time;
    const timeRatio = (time - prevItem.time) / timeDiff;

    const result: any = {};
    keys.forEach(key => {
      let prevValue = prevItem[key] || 0;
      let nextValue = nextItem[key] || 0;

      if (key === "angle") {
        result[key] = lerpAngle(prevValue, nextValue, timeRatio);
      } else {
        result[key] = prevValue + (nextValue - prevValue) * timeRatio;
      }
    });

    return result;
  };

  const lerpAngle = (a: number, b: number, t: number) => {
    a = ((a % 360) + 360) % 360;
    b = ((b % 360) + 360) % 360;

    let diff = b - a;

    if (diff > 180) {
      diff -= 360;
    } else if (diff < -180) {
      diff += 360;
    }

    const result = a + diff * t;
    return ((result % 360) + 360) % 360;
  };

  const updateCharacterPositions = useCallback(
    (timeToUse = progressRef.current) => {
      if (!replayData || !replayData.characters) {
        setCharacterStates([]);
        return;
      }

      const newCharacterStates = replayData.characters.map(character => interpolateCharacterData(character, timeToUse));

      setCharacterStates(newCharacterStates);
    },
    [replayData]
  );

  const animate = useCallback(
    (timestamp: number) => {
      if (timestamp - lastUpdateTimeRef.current >= REPLAY_RENDER_INTERVAL_MS) {
        updateCharacterPositions();
        lastUpdateTimeRef.current = timestamp;
      }

      if (isPlaying) {
        animationFrameRef.current = requestAnimationFrame(animate);
      }
    },
    [isPlaying, updateCharacterPositions]
  );

  useLayoutEffect(() => {
    if (!isPlaying) {
      progressRef.current = progress;
    }

    const hasReplayChanged = lastReplayDataRef.current !== replayData;

    if (hasReplayChanged) {
      lastReplayDataRef.current = replayData;
      if (!replayData?.characters) {
        setCharacterStates([]);
        return;
      }
      updateCharacterPositions(progress);
      return;
    }

    if (!isPlaying && replayData?.characters) {
      updateCharacterPositions(progress);
    }
  }, [progress, replayData, isPlaying, updateCharacterPositions]);

  useEffect(() => {
    if (isPlaying) {
      lastUpdateTimeRef.current = performance.now();
      animationFrameRef.current = requestAnimationFrame(animate);
    } else {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isPlaying, animate, updateCharacterPositions]);

  const fieldToScreen = (x: number, y: number, screenWidth: number, screenHeight: number) => {
    const centerX = screenWidth / 2;
    const centerY = screenHeight / 2;

    const scaleX = screenWidth / fieldDimensions.width;
    const scaleY = screenHeight / fieldDimensions.height;
    const scale = Math.min(scaleX, scaleY) * 0.8;

    return {
      x: centerX + x * scale,
      y: centerY + y * scale,
      scale,
    };
  };

  return {
    characterStates,
    fieldDimensions,
    fieldToScreen,
    replayDuration: replayData?.duration || 0,
  };
};

export default useReplayRenderer;
